import { useCallback, useEffect } from "react"
import { ModalBody, ModalFooter } from "@mezo-org/mezo-clay"
import { Address } from "viem"

import { setDepositBridged, useDappDispatch } from "#/redux-state"
import FailedActionModalBody from "#/shared/components/modals/FailedActionModalBody"
import WaitingForActionModalBody from "#/shared/components/modals/WaitingForActionModalBody"
import { ETHEREUM_CHAIN_ID } from "#/shared/constants"
import useBridgeDeposit from "#/shared/hooks/bridge/useBridgeDeposit"
import { SwitchChainButton } from "#/ui/Button"

import { ComponentStepProps, StepperModalButton } from "../StepperModal"

import BridgeSummary from "./BridgeSummary"

type BridgeDepositProps = {
  amount: bigint
  depositId: number
  tokenAddress: Address
} & ComponentStepProps

export default function BridgeDeposit({
  amount,
  depositId,
  tokenAddress,
  onClickContinue,
}: BridgeDepositProps) {
  const {
    bridgeDeposit,
    isPending,
    isWaitingForTxReceipt,
    isError,
    isTxConfirmed,
  } = useBridgeDeposit(tokenAddress, amount, depositId)

  const bridgeDepositHandler = useCallback(async () => {
    await bridgeDeposit()
  }, [bridgeDeposit])

  const dispatch = useDappDispatch()

  useEffect(() => {
    if (!isTxConfirmed) return

    dispatch(setDepositBridged({ depositId }))
    onClickContinue()
  }, [isTxConfirmed, onClickContinue, depositId, dispatch])

  if (isPending || isWaitingForTxReceipt)
    return <WaitingForActionModalBody title="Waiting for transaction..." />

  if (isError)
    return (
      <FailedActionModalBody
        title="Something went wrong"
        description="Please try again."
        nextStepButtonLabel="Try again"
        onClickNextStep={bridgeDepositHandler}
      />
    )

  return (
    <>
      <ModalBody>
        <BridgeSummary depositId={depositId} />
      </ModalBody>
      <ModalFooter>
        <SwitchChainButton
          width="100%"
          size="large"
          shape="default"
          label="bridge"
          chainId={ETHEREUM_CHAIN_ID}
        >
          <StepperModalButton onClick={bridgeDepositHandler}>
            Confirm
          </StepperModalButton>
        </SwitchChainButton>
      </ModalFooter>
    </>
  )
}
